import { Box, SxProps } from '@mui/material'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import ReactPlayer from 'react-player'
import { BaseReactPlayerProps } from 'react-player/base'

export type VideoFullScreenRef = {
  init: (params: BaseReactPlayerProps) => void
  handleOnReadyVideo: () => void
  playVideo: () => void
  pauseVideo: () => void
  src: BaseReactPlayerProps['url']
}

type VideoFullScreenProps = {
  /** 音声をミュートするかどうか。iOSブラウザでは音の自動再生が行えないため、動画を自動再生するにはにミュートする必要がある */
  shouldMute: boolean
  sx?: SxProps
}

export const VideoFullScreen = forwardRef<
  VideoFullScreenRef,
  VideoFullScreenProps
>(({ sx, shouldMute }, ref) => {
  const [reactPlayerProps, setReactPlayerProps] =
    useState<BaseReactPlayerProps>({})
  const [shouldPlay, setShouldPlay] = useState<boolean>(false)
  const [isReady, setIsReady] = useState<boolean>(false)
  const videoRef = useRef<HTMLDivElement>()
  const playerRef = useRef<ReactPlayer>(null)

  useImperativeHandle(ref, () => ({
    init(props) {
      setReactPlayerProps(props)
    },
    handleOnReadyVideo() {
      setIsReady(true)
    },
    playVideo() {
      setShouldPlay(true)
    },
    pauseVideo() {
      setShouldPlay(false)
    },
    src: reactPlayerProps.url,
  }))

  useEffect(() => {
    // ReactPlayer内部のplayerを用いて動画リソースを読み込んでから再生する
    const videoElm =
      playerRef.current?.getInternalPlayer() ||
      videoRef.current?.querySelector('video')
    const canPlay = videoElm && isReady
    if (!canPlay) return

    if (shouldPlay) {
      videoElm.play()
    } else {
      videoElm.pause()
    }
  }, [isReady, shouldPlay])

  useEffect(() => {
    const videoElm =
      playerRef.current?.getInternalPlayer() ||
      videoRef.current?.querySelector('video')
    if (!videoElm) return

    /**
     * HACK:
     * iOSのNotAllowedError対策
     * React-Player下のvideo要素にmuted属性を直接指定する
     */
    if (videoElm && shouldMute) {
      videoElm.setAttribute('muted', 'muted')
    }
  }, [reactPlayerProps, shouldMute])

  return (
    <Box
      className="videoFullScreen"
      sx={{
        display: 'grid',
        gridTemplateRows: '1fr',
        height: '100vh',
        overflow: 'hidden',
        margin: 'auto',
        maxWidth: 600,
        opacity: shouldPlay ? 1 : 0,
        position: 'fixed',
        zIndex: 12,
        width: '100%',
        inset: 0,
        pointerEvents: shouldPlay ? 'auto' : 'none',
        video: {
          objectFit: 'cover',
        },
        ...sx,
      }}
      ref={videoRef}
    >
      <ReactPlayer
        {...reactPlayerProps}
        ref={playerRef}
        width="100%"
        height="100%"
        playsInline
        // Safariでのみ発生するNotAllowedErrorを回避するため音声をミュートする
        muted={shouldMute}
      />
    </Box>
  )
})
